/**
 * Turns loaded data into the ranked list on the Diagnosis tab.
 *
 * Runs client-side today. Only the WireGuard finding is backed by a real
 * endpoint; the others appear when their gap endpoint (or sample data) returns
 * something. Sorting is by sites affected, descending, so the fix with the
 * widest blast radius is first — that is the one to do before anything else.
 */
import { getAccessibleGroups } from "../../WireGuardTunnels/wireGuardGroupUtils";
import { primaryHost } from "./integrationEndpoints";
import { sitePorts } from "./probePrefill";
import { connectionKindForSite, isUnderGroup } from "./useScope";
import { getArmedStatusPresentation } from "../../Sites/SitesPage/SitesPageUtils/armedStatusPresentation";
import {
  AlarmReceiver,
  AlertsReceived,
  CameraLastAlarms,
  CameraLiveness,
  ConnectionError,
  Finding,
  FindingOwner,
  GapResult,
  RefreshStatus,
  SettingsCompare,
  SiteConnectionDetail,
  SiteToSiteConnection,
  TroubleshootingSite,
  WireGuardPeerTelemetry,
  WireGuardTunnel,
} from "../troubleshootingInterfaces";
import { formatDateTime, formatRelative, pluralize } from "./formatters";
import { groupStalePeersByServer } from "./wireGuardHealth";
import { RuleEvaluation } from "./smtpFilenameRule";
import { familyHasConnectorHeartbeat, siteFamily } from "./siteFamily";
import {
  SAVED_LOGIN_EVIDENCE_MINUTES,
  readSavedLoginStatus,
  savedLoginSystemLabel,
} from "./savedLoginTest";
import {
  ConnectionErrorOwner,
  connectionErrorAdvice,
  lastWorkingServerId,
} from "./connectionErrorAdvice";
import {
  KEYFRAME_INTERVAL_WARN_SECONDS,
  formatKeyframeSeconds,
  keyframeIntervalHint,
} from "../../Sites/SiteAboutPage/SiteAboutPageUtils/keyframeInterval";

/** Heartbeat cadence is ~5 min (site-status-poller); two missed = stale. */
export const CONNECTOR_STALE_MINUTES = 10;
/**
 * Every puller re-uploads its preview image every 5 min, only when a new
 * frame arrived: three missed uploads on a continuous camera = frames stopped.
 * Bandwidth (PyAV only) is the fallback when there is no preview time.
 */
export const LAST_FRAME_STALE_MINUTES = 15;

export interface FindingsInput {
  sites: TroubleshootingSite[];
  /** Set when scope is one site. */
  siteDetail: SiteConnectionDetail | null;
  tunnels: WireGuardTunnel[];
  telemetry: GapResult<Record<number, WireGuardPeerTelemetry>> | null;
  siteToSite: GapResult<SiteToSiteConnection[]> | null;
  receivers: GapResult<AlarmReceiver[]> | null;
  /** Filename-rule evaluation for the scoped SMTP site, when we have samples. */
  smtp: {
    siteId: number;
    siteName: string;
    rows: RuleEvaluation[];
    counts: number[];
  } | null;
  getServerName: (serverId: number | null) => string;
  /** Alerts processed for the scope in the last hour, when loaded. */
  alertsReceived?: AlertsReceived | null;
  /** Per-camera last alarm for the one site in scope, when loaded. */
  cameraLastAlarms?: CameraLastAlarms | null;
  /** Per-camera "working now" signals for the one site (ENG-2046). */
  cameraLiveness?: CameraLiveness | null;
  smtpSiteIds?: number[];
  /**
   * "Compare to when it last worked" (ENG-2244), only when it has already
   * been run for this site: supplies the Server ID that last worked.
   */
  settingsCompare?: SettingsCompare | null;
  /**
   * The site's last saved-login result (Test saved login, or any camera-list
   * refresh), when loaded. Never a finding of its own here; it sharpens the
   * silent-connector one.
   */
  savedLogin?: { siteId: number; status: RefreshStatus | null } | null;
  nowMs?: number;
}

/** The advice's owner on the Finding scale: "unknown" is either side's. */
const loginOwner = (owner: ConnectionErrorOwner): FindingOwner =>
  owner === "unknown" ? "shared" : owner;

/** "http_502" → "HTTP 502" for the metric; other codes as sent. */
const metricCode = (code: string): string => {
  const http = /^http_(\d{3})$/i.exec(code);
  return http ? `HTTP ${http[1]}` : code;
};

const okData = <T>(result: GapResult<T> | null): T | null =>
  result && result.status === "ok" ? result.data : null;

/**
 * Which sites sit behind a tunnel, on positive evidence only. With one site
 * in scope the site's own `wireguard` field is authoritative. Otherwise the
 * tunnel's `network_path/` row says exactly which sites ride it
 * (`customer_ids`, ENG-1674). A tunnel with no such row claims no site: a
 * tunnel names a group, and assuming every site in it rides the tunnel
 * inflated the blast radius (QA DG-19). See `unconfirmedSitesForTunnel`.
 */
export const sitesForTunnel = (
  tunnel: WireGuardTunnel,
  sites: TroubleshootingSite[],
  siteDetail: SiteConnectionDetail | null,
  paths: SiteToSiteConnection[] = []
): number[] => {
  // With one site in scope the tunnels passed in are already the site's own,
  // resolved by subnet (siteTunnel.ts).
  if (siteDetail) return [siteDetail.id];
  const exact = paths.find(
    (path) =>
      path.kind === "wireguard" &&
      path.site_ids_exact &&
      path.wireguard_tunnel === tunnel.id
  );
  return exact ? exact.site_ids : [];
};

/**
 * The upper bound where `sitesForTunnel` has no evidence: the sites listed
 * under the tunnel's group. Only ever shown as "up to N, not confirmed";
 * never ranked on and never listed as affected.
 */
export const unconfirmedSitesForTunnel = (
  tunnel: WireGuardTunnel,
  sites: TroubleshootingSite[],
  siteDetail: SiteConnectionDetail | null,
  paths: SiteToSiteConnection[] = []
): number[] => {
  if (siteDetail || tunnel.group == null) return [];
  if (sitesForTunnel(tunnel, sites, siteDetail, paths).length) return [];
  const groups = getAccessibleGroups();
  return sites
    .filter((site) => isUnderGroup(tunnel.group ?? null, site.groupId, groups))
    .map((site) => site.id);
};

/** ISO timestamp -> the finding's on-screen `since` plus the raw value. */
const sinceFields = (iso: string | null | undefined) => ({
  since: iso ? formatDateTime(iso) : null,
  sinceIso: iso || null,
});

export const buildFindings = (input: FindingsInput): Finding[] => {
  const nowMs = input.nowMs ?? Date.now();
  const findings: Finding[] = [];
  const paths = okData(input.siteToSite) ?? [];

  // ---- WireGuard: stale peers grouped by gateway (REAL data) ---------------
  const telemetry = okData(input.telemetry);
  const staleByServer = groupStalePeersByServer(input.tunnels, nowMs);
  staleByServer.forEach((peers, serverId) => {
    const names = peers.map((p) => p.name);
    const affected = Array.from(
      new Set(
        peers.flatMap((p) =>
          sitesForTunnel(p, input.sites, input.siteDetail, paths)
        )
      )
    );
    // No positive evidence for any peer: say how many sites it could be,
    // without listing them or ranking on the guess.
    const unconfirmed = affected.length
      ? []
      : Array.from(
          new Set(
            peers.flatMap((p) =>
              unconfirmedSitesForTunnel(p, input.sites, input.siteDetail, paths)
            )
          )
        );
    const unconfirmedGroup =
      unconfirmed.length && peers[0].group != null
        ? getAccessibleGroups().find((g) => g.id === peers[0].group)?.name ??
          null
        : null;
    const oldest = peers
      .map((p) => p.last_handshake)
      .filter((h): h is string => Boolean(h))
      .sort()[0];
    const gateway = input.getServerName(serverId);
    const healthyOnSameGateway = input.tunnels.some(
      (t) =>
        t.server === serverId &&
        !peers.includes(t) &&
        t.last_handshake &&
        nowMs - new Date(t.last_handshake).getTime() <= 15 * 60 * 1000
    );
    const firstTelemetry = telemetry ? telemetry[peers[0].id] : undefined;
    const initiations = firstTelemetry?.initiations_without_handshake;
    const endpoint = firstTelemetry?.endpoint;

    const bodyParts = [
      peers.length === 1
        ? `The peer is still configured on gateway ${gateway}, but its last completed handshake was ${formatRelative(
            oldest,
            nowMs
          )}.`
        : `All ${
            peers.length
          } peers are still configured on gateway ${gateway}, but their last completed handshakes were ${formatRelative(
            oldest,
            nowMs
          )} or longer ago.`,
      initiations === true && endpoint
        ? `Your endpoint ${endpoint} is still sending handshake initiations but the gateway's responses are not being acknowledged, which almost always means the NAT or firewall in front of your WireGuard peer changed state.`
        : `A stale handshake with the gateway healthy usually means the NAT or firewall in front of your WireGuard peer changed state, or the edge device rebooted without bringing the interface back up.`,
      healthyOnSameGateway
        ? `Other peers on ${gateway} are handshaking normally, which rules out the gateway itself.`
        : "",
      unconfirmed.length
        ? `Up to ${pluralize(unconfirmed.length, "site")}${
            unconfirmedGroup ? ` in ${unconfirmedGroup}` : ""
          } may connect through ${
            peers.length === 1 ? "this tunnel" : "these tunnels"
          }, not confirmed: we have no record of which sites ride it.`
        : "",
    ].filter(Boolean);

    findings.push({
      id: `wg-stale-${serverId ?? "none"}`,
      severity: "bad",
      owner: "shared",
      kind: "connection",
      connectionKind: "wireguard",
      title:
        peers.length === 1
          ? `WireGuard peer ${names[0]} stopped handshaking`
          : `WireGuard peers ${names.slice(0, 2).join(" and ")}${
              peers.length > 2 ? ` and ${peers.length - 2} more` : ""
            } stopped handshaking`,
      ...sinceFields(oldest),
      body: bodyParts.join(" "),
      steps: [
        `On the router in front of your WireGuard device, confirm UDP 51820 is allowed out to ${gateway} and that no NAT or port-mapping rule changed recently.`,
        "Restart the WireGuard interface on your edge device (wg-quick down, then up). With keepalive set, a fresh handshake completes within a minute.",
        "Still stale? Open the WireGuard Debugger for the tunnel and copy the report. Actuate can re-issue the peer config from our side; the public key does not need to change.",
      ],
      affectedSiteIds: affected,
      metric: affected.length
        ? {
            value: String(affected.length),
            label: pluralize(
              affected.length,
              "site affected",
              "sites affected"
            ),
          }
        : {
            value: String(peers.length),
            label: pluralize(peers.length, "peer stale", "peers stale"),
          },
      // "View affected sites" only when there are sites we can name.
      actions: affected.length
        ? ["affected", "wg-debug", "copy"]
        : ["wg-debug", "copy"],
      tunnelId: peers[0].id,
    });
  });

  // ---- Site-to-Site: connections AWS reports down ----------------------------
  // On the glance, not the drawn tunnel rows: an "unknown" glance draws one
  // UNKNOWN row, which is not the same as down.
  const s2s = paths.filter(
    (connection) => connection.kind === "aws_site_to_site"
  );
  const downByPeer = new Map<string, SiteToSiteConnection[]>();
  s2s.forEach((connection) => {
    if (connection.status.status === "down") {
      const list = downByPeer.get(connection.customer_gateway_ip) || [];
      list.push(connection);
      downByPeer.set(connection.customer_gateway_ip, list);
    }
  });
  downByPeer.forEach((connections, peerIp) => {
    const affected = Array.from(
      new Set(connections.flatMap((c) => c.site_ids))
    );
    const since = connections
      .flatMap((c) => c.tunnels.map((t) => t.last_status_change))
      .filter((v): v is string => Boolean(v))
      .sort()[0];
    const otherPeersHealthy = s2s.some(
      (c) =>
        c.customer_gateway_ip !== peerIp &&
        c.tunnels.some((t) => t.state === "UP")
    );
    findings.push({
      id: `s2s-down-${peerIp}`,
      severity: "bad",
      owner: "customer",
      kind: "connection",
      connectionKind: "site-to-site",
      title: `${pluralize(
        connections.length,
        "VPN tunnel",
        "VPN tunnels"
      )} to your peer ${peerIp} ${
        connections.length === 1 ? "is" : "are"
      } down`,
      ...sinceFields(since),
      body: `${connections
        .map((c) => c.name.split(",")[0])
        .join(
          " and "
        )} report every IPsec tunnel down. Actuate's gateway is up${
        otherPeersHealthy
          ? " and your other peer has stayed healthy the whole time"
          : ""
      }, so this is isolated to the firewall or router terminating IPsec at ${peerIp}, or its internet circuit. Alarms from the affected sites are queued and deliver on their own within about 5 minutes of the tunnel returning.`,
      steps: [
        `Check the IPsec device at ${peerIp} and its upstream circuit with your ISP.`,
        "Do not disarm and re-arm sites; it does not help and prolongs recovery.",
        "Come back here: the tunnel rows turn green as soon as AWS sees the tunnel re-establish.",
      ],
      affectedSiteIds: affected,
      metric: {
        value: String(affected.length),
        label: pluralize(affected.length, "site affected", "sites affected"),
      },
      actions: ["affected", "s2s-check", "copy"],
      connectionId: connections[0].id,
    });
  });

  // ---- Direct paths whose probe target is not answering ---------------------
  paths
    .filter(
      (connection) =>
        connection.kind === "direct" && connection.status.status === "down"
    )
    .forEach((connection) => {
      const target = connection.probe_target
        ? `${connection.probe_target}${
            connection.probe_port ? `:${connection.probe_port}` : ""
          }`
        : connection.name;
      const what = connection.probe_label
        ? `${connection.probe_label} at ${target}`
        : target;
      findings.push({
        id: `direct-down-${connection.id}`,
        severity: "bad",
        owner: "customer",
        kind: "connection",
        connectionKind: "direct",
        title: `Your server at ${target} is not reachable from Actuate`,
        since: null,
        body: `${connection.name}: we reach ${what} over the internet, and it is not answering. Cameras behind it cannot be analysed until it answers again. Actuate's side is up, so this is the server, its port forward, or its internet circuit.`,
        steps: [
          "Confirm the server or NVR is powered on and on the network.",
          `Check the port forward for ${target} on your router or firewall, and that Actuate's addresses are allowed through.`,
          "Run the connection check on the Connections tab: the reachability step says whether the port answers now.",
        ],
        affectedSiteIds: connection.site_ids,
        metric: {
          value: String(connection.site_ids.length),
          label: pluralize(
            connection.site_ids.length,
            "site affected",
            "sites affected"
          ),
        },
        actions: ["affected", "s2s-check", "copy"],
        connectionId: connection.id,
      });
    });

  // ---- Arm state of the one site in scope ----------------------------------
  // "Not receiving alarms" is by design for a disarmed or inactive site, and
  // "Armed – not running" is the connector, not the network. Either is the
  // answer before any connection check, and it is self-serve from the site
  // page. Read through the same presentation the site page uses (SUP-515).
  const armedDetail = input.siteDetail;
  const arming =
    armedDetail && (armedDetail.armed != null || armedDetail.armed_status)
      ? getArmedStatusPresentation(
          armedDetail.armed_status,
          armedDetail.armed,
          armedDetail.active ?? true
        )
      : null;
  // WARNING ("armed, no motion in 10 min") is not a fault of its own: when the
  // site is silent the unreachable finding below is the answer, and when it
  // is alerting there is nothing to say. So no arming card for it.
  if (
    armedDetail &&
    arming &&
    arming.tone !== "armed" &&
    arming.tone !== "manual" &&
    arming.tone !== "unknown" &&
    arming.tone !== "warning"
  ) {
    const inactive = armedDetail.active === false;
    const disarmed = arming.tone === "disarmed";
    const disarming = arming.status === "DISARMING";
    // A disarmed site generates no alarms, so recent alerts mean the stored
    // arm state is the stale signal, not the alerts (QA, ENG-2187).
    const recentAlerts = input.alertsReceived?.count ?? 0;
    if (disarmed && !inactive && recentAlerts > 0 && input.alertsReceived) {
      const alerts = input.alertsReceived;
      const count = `${
        alerts.truncated ? "at least " : ""
      }${recentAlerts.toLocaleString()} alert${recentAlerts === 1 ? "" : "s"}`;
      const span =
        alerts.windowHours === 1
          ? "the last hour"
          : `the last ${alerts.windowHours} hours`;
      findings.push({
        id: `arming-${armedDetail.id}`,
        severity: "info",
        owner: "none",
        kind: "arming",
        title: `${armedDetail.name} reads Disarmed, but ${count} arrived in ${span}`,
        since: null,
        body: `The arm state reads Disarmed, but ${count} arrived in ${span} — the stored state may be out of date. A disarmed site generates no alarms, so these alerts mean analysis is running.`,
        steps: [
          "Open the site page and check its arm state. Its schedule and Action logs show when it last armed or disarmed.",
        ],
        affectedSiteIds: [armedDetail.id],
        metric: { value: arming.label, label: "arm state" },
        actions: ["open-site", "copy"],
        siteId: armedDetail.id,
        siteName: armedDetail.name,
      });
    } else {
      // Transitional states are the user's to nudge from the site page; only a
      // connector that is armed and not running is Actuate's.
      // Disarmed or inactive is an explanation, not a fault: no warning and no
      // "fix" wording (Jacob). Transitional and error states still warn.
      findings.push({
        id: `arming-${armedDetail.id}`,
        severity:
          disarmed || inactive
            ? "info"
            : arming.tone === "error"
            ? "bad"
            : "warn",
        owner:
          disarmed || inactive
            ? "none"
            : arming.transitional
            ? "self-serve"
            : "actuate",
        kind: "arming",
        title: inactive
          ? `${armedDetail.name} is inactive, so it does not arm or send alarms`
          : disarmed
          ? `${armedDetail.name} is disarmed, so no alarms are expected`
          : disarming
          ? `${armedDetail.name} is disarming, so no alarms are expected`
          : arming.transitional
          ? `${armedDetail.name} is still arming`
          : `${armedDetail.name} is ${arming.label.toLowerCase()}`,
        since: null,
        // The presentation's description already carries the settle hint.
        body: `${arming.description}${
          disarmed || inactive
            ? " Nothing is wrong with the connection or the cameras: analysis is off until the site is armed."
            : disarming
            ? " While it is not armed no alarm is generated, so this alone explains a quiet site."
            : arming.transitional
            ? " No alarms are generated until it is back up."
            : " Alarms cannot be generated while the connector is in this state, whatever the network looks like."
        }`,
        steps: inactive
          ? [
              "Open the site page and activate the site, then arm it or let its schedule arm it.",
            ]
          : disarmed
          ? [
              "To arm it now, use the site page. Its schedule and Action logs show who disarmed it and when.",
            ]
          : disarming
          ? [
              "If it has been disarming for more than 10 minutes the state is stuck: open the site page and arm it, or disarm it again, to reset it. Then run the checks again.",
            ]
          : arming.transitional
          ? [
              "Wait for it to finish arming, then run the checks again. If it is still arming after 10 minutes, open the site page and arm it again.",
            ]
          : [
              "Open the site page: if the arm state does not recover on its own, restart the site from there.",
              "If it stays in this state, copy this report into a ticket; the connector needs looking at on Actuate's side.",
            ],
        affectedSiteIds: [armedDetail.id],
        metric: { value: arming.label, label: "arm state" },
        actions: ["open-site", "copy"],
        siteId: armedDetail.id,
        siteName: armedDetail.name,
      });
    }
  }

  // ---- Connector and cameras, one site (ENG-2046) --------------------------
  // Evidence comes from signals that are refreshed while things work: the
  // site heartbeat (connector reporting, ~5 min) and, for pull families, each
  // camera's bandwidth timestamp and broken-frame flag. Never from a lack of
  // alerts or motion: a quiet scene sends none, and Milestone, SMTP, clips and
  // AutoPatrol are event- or schedule-driven. The camera "connected" flag is
  // written at launch only and is not used.
  const detail = input.siteDetail;
  const live =
    detail && input.cameraLiveness && input.cameraLiveness.siteId === detail.id
      ? input.cameraLiveness
      : null;
  const family = detail ? siteFamily(detail.integration_type_name) : "other";
  const shouldBeRunning =
    arming != null &&
    (arming.tone === "armed" ||
      arming.tone === "warning" ||
      arming.tone === "manual") &&
    detail?.active !== false;
  const ageMin = (iso: string | null | undefined) =>
    iso ? (nowMs - new Date(iso).getTime()) / 60_000 : null;
  const lastSeen = live?.connector?.lastSeenAt ?? null;
  const connectorAge = ageMin(lastSeen);
  const connectorFresh =
    connectorAge != null && connectorAge <= CONNECTOR_STALE_MINUTES;
  const siteTunnelStale = detail != null && staleByServer.size > 0;

  // ---- VMS login failures, with the VMS's own answer (ENG-2252) -----------
  // Pushed before the generic "no video" finding so the specific cause reads
  // first; that finding then points here instead of guessing at the login.
  const loginErrorByCamera = new Map<number, ConnectionError>();
  let siteLoginError: ConnectionError | null = null;
  if (detail && live) {
    const lastWorking = lastWorkingServerId(
      input.settingsCompare && input.settingsCompare.siteId === detail.id
        ? input.settingsCompare
        : null
    );
    const adviceFor = (error: ConnectionError) =>
      connectionErrorAdvice(error, { lastWorkingServerId: lastWorking, nowMs });
    const newest = (errors: ConnectionError[]) =>
      errors
        .slice()
        .sort((a, b) => (b.at ?? "").localeCompare(a.at ?? ""))[0] ?? null;

    const rows = (live.cameras ?? []).filter(
      (camera) => camera.lastConnectionError
    );
    rows.forEach((camera) =>
      loginErrorByCamera.set(
        camera.cameraId,
        camera.lastConnectionError as ConnectionError
      )
    );
    siteLoginError =
      live.connectionError ??
      newest(
        rows
          .map((camera) => camera.lastConnectionError as ConnectionError)
          .filter((error) => error.scope === "site")
      );
    const stoppedNote =
      arming && !shouldBeRunning
        ? " The site isn't running now, so this is the last failure recorded before it stopped."
        : "";
    // "Failing since {date} ({relative}); latest failed login {relative}."
    // The streak start leads; the latest failure is secondary (ENG-2252).
    const timing = (advice: ReturnType<typeof adviceFor>) =>
      advice.failingSince
        ? `${advice.failingSince}; latest failed login ${
            advice.raw.at ? advice.raw.atRelative : "at an unknown time"
          }.`
        : "When it started failing is unknown.";
    const sinceOf = (advice: ReturnType<typeof adviceFor>) =>
      sinceFields(advice.raw.firstFailedAt);

    if (siteLoginError) {
      const advice = adviceFor(siteLoginError);
      findings.push({
        id: `login-site-${detail.id}`,
        severity: "bad",
        owner: loginOwner(advice.owner),
        kind: "login",
        title: `${advice.title} at ${detail.name}`,
        ...sinceOf(advice),
        body: `The connector can't log in to the VMS for ${
          detail.name
        }, so no camera there is analysed. ${timing(advice)}${stoppedNote}`,
        steps: [advice.action],
        affectedSiteIds: [detail.id],
        metric: {
          value: metricCode(advice.raw.code),
          label: "VMS login error",
        },
        actions: ["copy"],
        siteId: detail.id,
        siteName: detail.name,
        detailLine: advice.detailLine,
      });
    }

    // Camera-scope failures, one finding per identical VMS answer.
    const groups = new Map<
      string,
      { error: ConnectionError; names: string[]; ids: number[] }
    >();
    rows.forEach((camera) => {
      const error = camera.lastConnectionError as ConnectionError;
      if (error.scope === "site") return;
      const key = `${error.code}\u0000${(error.detail ?? "").trim()}`;
      const group = groups.get(key);
      if (group) {
        group.names.push(camera.cameraName);
        group.ids.push(camera.cameraId);
        // Latest failure from the newest row; streak start from the oldest.
        const start = (e: ConnectionError) => e.first_failed_at || e.at || "";
        const earliest =
          start(error) &&
          (!start(group.error) || start(error) < start(group.error))
            ? start(error)
            : start(group.error);
        if ((error.at ?? "") > (group.error.at ?? "")) group.error = error;
        group.error = { ...group.error, first_failed_at: earliest || null };
      } else
        groups.set(key, {
          error,
          names: [camera.cameraName],
          ids: [camera.cameraId],
        });
    });
    groups.forEach(({ error, names, ids }) => {
      const advice = adviceFor(error);
      const listed = names.slice(0, 5).join(", ");
      const more = names.length > 5 ? ` and ${names.length - 5} more` : "";
      findings.push({
        id: `login-camera-${detail.id}-${ids[0]}-${error.code}`,
        severity: "bad",
        owner: loginOwner(advice.owner),
        kind: "login",
        title:
          names.length === 1
            ? `${advice.title}: ${names[0]}`
            : `${advice.title}: ${names.length} cameras`,
        ...sinceOf(advice),
        body: `The connector's login for ${listed}${more} is failing, so ${
          names.length === 1 ? "that camera isn't" : "those cameras aren't"
        } analysed. ${timing(advice)}${stoppedNote}`,
        steps: [advice.action],
        affectedSiteIds: [detail.id],
        metric: {
          value: metricCode(advice.raw.code),
          label: pluralize(names.length, "camera failing", "cameras failing"),
        },
        actions: ["copy"],
        siteId: detail.id,
        siteName: detail.name,
        detailLine: advice.detailLine,
      });
    });
  }

  // A stale heartbeat alone is not a dead connector (QA DG-21/24): the
  // heartbeat poller can lag while the connector keeps working. A dead
  // connector produces neither fresh frames nor alerts, so either one rules
  // the "hasn't reported" finding out. A preview the connector uploaded after
  // its last heartbeat proves the heartbeat is the stale signal, however old
  // the preview: an event-driven (SMTP) camera can go hours between frames.
  // Not `lastImageReceivedAt`: the SMTP receiver writes that before the
  // connector runs, so it says nothing about the connector (ENG-2187).
  const lastSeenMs = lastSeen ? new Date(lastSeen).getTime() : NaN;
  const connectorEvidentlyWorking =
    (live?.cameras ?? []).some((camera) => {
      if (!camera.lastFrameAt) return false;
      const frameMs = new Date(camera.lastFrameAt).getTime();
      if (!Number.isFinite(lastSeenMs) || frameMs > lastSeenMs) return true;
      const age = ageMin(camera.lastFrameAt);
      return age != null && age <= LAST_FRAME_STALE_MINUTES;
    }) || (input.alertsReceived?.count ?? 0) > 0;
  // A saved login that worked within the hour rules out the VMS and the
  // credentials, which leaves the connector itself (ENG-2187).
  const loginOutcome =
    detail && input.savedLogin && input.savedLogin.siteId === detail.id
      ? readSavedLoginStatus(input.savedLogin.status)
      : null;
  const loginOkAt =
    loginOutcome?.state === "ok" &&
    (nowMs - loginOutcome.at * 1000) / 60_000 <= SAVED_LOGIN_EVIDENCE_MINUTES
      ? new Date(loginOutcome.at * 1000).toISOString()
      : null;
  const loginLabel = savedLoginSystemLabel(detail?.integration_type_name);
  const loginSystem = loginLabel === "system" ? "VMS" : loginLabel;
  if (
    detail &&
    live &&
    shouldBeRunning &&
    familyHasConnectorHeartbeat(family) &&
    live.connector?.inSchedule !== false &&
    connectorAge != null &&
    connectorAge > CONNECTOR_STALE_MINUTES &&
    !connectorEvidentlyWorking
  ) {
    findings.push({
      id: `connector-silent-${detail.id}`,
      severity: "bad",
      owner: "actuate",
      kind: "arming",
      // The time is in `since` (shown in the viewer's locale, written as
      // ISO in the report), not baked into the title (QA DG-35).
      title: `The connector for ${detail.name} has stopped reporting`,
      ...sinceFields(lastSeen),
      body: `${
        detail.name
      } should be running, but its connector has not sent a heartbeat for ${formatRelative(
        lastSeen as string,
        nowMs
      ).replace(
        / ago$/,
        ""
      )}. The arm state shown for the site keeps its last value when a connector stops, so it can still read as armed. No alarms are generated until the connector reports again.${
        loginOkAt
          ? ` Its ${loginSystem} accepted our login ${formatRelative(
              loginOkAt,
              nowMs
            )}, so the site and credentials are fine — this is the connector.`
          : ""
      }`,
      steps: loginOkAt
        ? [
            "Restart the connector: open the site page, press Sync, and reboot the site when it offers. If it has not reported within 10 minutes, copy this report into a ticket: the connector needs looking at on Actuate's side.",
          ]
        : [
            "Open the site page, press Sync, and reboot the site when it offers.",
            "If it has not reported within 10 minutes of the reboot, copy this report into a ticket: the connector needs looking at on Actuate's side.",
          ],
      affectedSiteIds: [detail.id],
      metric: {
        value: formatRelative(lastSeen as string, nowMs),
        label: "last heartbeat",
      },
      actions: ["open-site", "copy"],
      siteId: detail.id,
      siteName: detail.name,
    });
  } else if (
    detail &&
    live &&
    (live.signals.lastFrame || live.signals.bandwidth) &&
    shouldBeRunning &&
    (connectorFresh || live.connector == null) &&
    !siteTunnelStale &&
    live.cameras.some((camera) => camera.continuous)
  ) {
    // Only continuous cameras can be judged by the age of their last frame:
    // a motion-gated one legitimately goes quiet.
    const judged = live.cameras.filter((camera) => camera.continuous);
    const down = judged
      .map((camera) => {
        const lastVideo = camera.lastFrameAt ?? camera.bandwidthUpdatedAt;
        const age = ageMin(lastVideo);
        // A login failure is named on its own finding above; say so here.
        const loginError = siteLoginError
          ? null
          : loginErrorByCamera.get(camera.cameraId);
        const login = loginError ? `; login failing, ${loginError.code}` : "";
        if (camera.brokenFrame === true)
          return { camera, why: `returning broken frames${login}` };
        if (age != null && age > LAST_FRAME_STALE_MINUTES)
          return {
            camera,
            why: `no video since ${formatRelative(
              lastVideo as string,
              nowMs
            )}${login}`,
          };
        return null;
      })
      .filter(
        (
          row
        ): row is { camera: CameraLiveness["cameras"][number]; why: string } =>
          Boolean(row)
      );
    if (down.length > 0) {
      const all = down.length === judged.length;
      const kind = connectionKindForSite(detail, input.smtpSiteIds ?? []);
      // The address and ports the connector dials for this integration,
      // defaults included (integrationEndpoints).
      const host =
        primaryHost(detail) ||
        detail.server_ip ||
        detail.management_server_ip ||
        null;
      const ports = sitePorts(detail);
      const portText = ports.length
        ? `${ports.length === 1 ? "port" : "ports"} ${ports.join(", ")}`
        : "its camera ports";
      const listed = down
        .slice(0, 5)
        .map((row) => `${row.camera.cameraName} (${row.why})`);
      const more =
        down.length > listed.length
          ? ` and ${down.length - listed.length} more`
          : "";
      const siteTunnel = input.tunnels[0];
      const via =
        kind === "wireguard"
          ? siteTunnel
            ? `Its WireGuard tunnel ${siteTunnel.name} is handshaking, so the link to the site is up; the cameras or recorder on the far side are not delivering.`
            : "It is reached over WireGuard, but no tunnel's subnet contains its addresses, so the route to it could not be checked."
          : host
          ? `It is reached directly at ${host}.`
          : "";
      const vmsLogin = all && family === "vms-pull";
      findings.push({
        id: `unreachable-${detail.id}`,
        severity: all ? "bad" : "warn",
        owner: "customer",
        kind: "unreachable",
        connectionKind: kind,
        title: vmsLogin
          ? `The VMS at ${detail.name} isn't delivering video for any camera`
          : all
          ? `Actuate isn't receiving video from any camera at ${detail.name}`
          : `${down.length} of ${judged.length} cameras at ${detail.name} aren't delivering video`,
        since: null,
        body: [
          `The connector is running and reporting, but ${listed.join(
            ", "
          )}${more}.`,
          siteLoginError
            ? `The connector's VMS login is failing (${siteLoginError.code}); the login finding above names the cause, so start there.`
            : vmsLogin
            ? "Every camera stopped together, which usually means the connector cannot log in to the VMS or reach its server, not that the cameras themselves failed."
            : "",
          via,
        ]
          .filter(Boolean)
          .join(" "),
        steps: [
          siteLoginError
            ? "Fix the VMS login first, using the steps on the login finding above. Video returns on its own once the connector logs in."
            : vmsLogin
            ? "Check the VMS is up and that the Actuate user's password on it has not changed or expired."
            : host && kind !== "wireguard"
            ? `Check reachability: the Connectivity analyzer probes ${host} on ${portText} from Actuate. A closed or filtered port confirms it.`
            : "Confirm the cameras (or the recorder in front of them) are powered on and online locally.",
          kind === "wireguard"
            ? "Open the WireGuard Debugger for the tunnel and run it against one of these cameras: it tests reachability, login and the stream through the tunnel."
            : "If the ports answer, open Edit Camera and check the stream address and credentials.",
        ],
        affectedSiteIds: [detail.id],
        metric: {
          value: `${down.length}/${judged.length}`,
          label: "cameras without video",
        },
        actions:
          host && kind !== "wireguard" ? ["probe-host", "copy"] : ["copy"],
        probeHost: host && kind !== "wireguard" ? host : undefined,
        probePorts: host && kind !== "wireguard" ? ports : undefined,
        probeCameraIds:
          host && kind !== "wireguard"
            ? down.map((row) => row.camera.cameraId)
            : undefined,
        siteId: detail.id,
        siteName: detail.name,
      });
    }
  }

  // ---- Alarm receivers not accepting (GAP data) ----------------------------
  const receivers = okData(input.receivers) ?? [];
  receivers.forEach((receiver) => {
    if (
      receiver.health.listening === "listening" ||
      receiver.health.listening === "unknown"
    )
      return;
    const unreachableBecauseVpn = receiver.health.listening === "unreachable";
    if (unreachableBecauseVpn) return; // the connection finding already covers it
    const refused = receiver.health.listening === "refused";
    const fails = receiver.health.failures_last_hour;
    findings.push({
      id: `receiver-${receiver.id}`,
      severity: "bad",
      owner: "customer",
      kind: "receiver",
      global: receiver.via_connection == null && receiver.site_ids.length > 1,
      title: refused
        ? `Your ${receiver.label} is refusing alarms while the network is fine`
        : `Your ${receiver.label} ${receiver.host}:${receiver.port} is not accepting alarms`,
      ...sinceFields(receiver.health.last_accepted_at),
      body: refused
        ? `The network path is up, but the receiver at ${receiver.host}:${receiver.port} has refused recent alarm connections. This is the pattern that looks like "no alarms" from your side while everything on the network is green.`
        : `Actuate can reach your cameras and is generating alarms, but every delivery to ${
            receiver.host
          }:${
            receiver.port
          } has ${receiver.health.detail.toLowerCase()}. The address resolves, so the listener on the receiver is most likely stopped.${
            receiver.site_ids.length > 1
              ? " This receiver is shared by every site that alarms to it, so it affects sites whose network connection is otherwise fine."
              : ""
          }`,
      steps: [
        refused
          ? `Restart the receiving service on ${receiver.host}. Check its memory and disk first; it tends to refuse when out of resources.`
          : `Restart the listener on ${receiver.host}, or confirm your firewall still allows Actuate's sending addresses on port ${receiver.port}.`,
        "Press Send test alarm below. A delivered test proves the path end to end.",
        "Failed alarms are still visible under Alerts in the meantime.",
      ],
      affectedSiteIds: receiver.site_ids,
      metric:
        fails != null && fails > 0
          ? { value: fails.toLocaleString(), label: "failed in the last hour" }
          : {
              value: String(receiver.site_ids.length),
              label: pluralize(
                receiver.site_ids.length,
                "site affected",
                "sites affected"
              ),
            },
      actions: ["affected", "test-alarm", "copy"],
      receiverId: receiver.id,
    });
  });

  // ---- SMTP filename rule mismatch (GAP data for samples) ------------------
  if (input.smtp) {
    const { siteId, siteName, rows, counts } = input.smtp;
    const total = counts.reduce((sum, count) => sum + count, 0);
    const matchedCount = rows.reduce(
      (sum, row, index) =>
        row.state === "ok" ? sum + (counts[index] ?? 1) : sum,
      0
    );
    if (rows.length > 0 && matchedCount === 0) {
      findings.push({
        id: `smtp-mismatch-${siteId}`,
        severity: "bad",
        owner: "self-serve",
        kind: "smtp",
        connectionKind: "smtp",
        title: `${siteName}: images are arriving but we can't tell which camera sent them`,
        since: null,
        body: `This site uses one sender address for the whole NVR, so we read the camera name out of each attachment's filename using the site's filename rule. The current rule matches no camera in Actuate: ${
          total ? `${total} of ${total}` : "all"
        } recent images were filed as UNIDENTIFIED and raised no alarms. Nothing is wrong with the cameras or the network.`,
        steps: [
          "Open the SMTP Cameras tab. The recent-filenames table shows exactly what the NVR sends and what the rule reads.",
          "Pick the suggested rule or adjust yours until every recent filename turns green and maps to a camera.",
          "Save. New images are matched immediately; nothing to change on the NVR.",
        ],
        affectedSiteIds: [siteId],
        metric: {
          value: total ? String(total) : String(rows.length),
          label: total ? "images unidentified, 12 h" : "filenames unmatched",
        },
        actions: ["fix-smtp", "copy"],
        siteId,
      });
    }
  }

  // ---- Slow live-view start: long keyframe interval (ENG-2222) -------------
  // Live view (LL-HLS) can't serve its first segment until the camera sends a
  // keyframe, so a long I-frame interval means a long wait for video. Read
  // from the liveness rows already loaded for the one site: no extra
  // per-camera requests. Pushed last so real faults stay on top.
  if (detail && live) {
    live.cameras.forEach((camera) => {
      const seconds = camera.keyframeIntervalSeconds;
      if (
        seconds == null ||
        !Number.isFinite(seconds) ||
        seconds <= KEYFRAME_INTERVAL_WARN_SECONDS
      )
        return;
      // A lower bound means no second keyframe yet: at least `seconds`.
      const lowerBound = camera.keyframeIntervalIsLowerBound === true;
      const hint = keyframeIntervalHint({
        keyframe_interval_seconds: seconds,
        keyframe_interval_frames: camera.keyframeIntervalFrames ?? null,
        keyframe_interval_is_lower_bound: lowerBound,
      });
      findings.push({
        id: `keyframe-interval-${camera.cameraId}`,
        severity: "warn",
        owner: "customer",
        kind: "camera",
        title: lowerBound
          ? `Slow live view start: no keyframe for over ${formatKeyframeSeconds(
              seconds
            )} s`
          : `Slow live view start: keyframe every ${formatKeyframeSeconds(
              seconds
            )} s`,
        since: null,
        body: [
          `${camera.cameraName}: ${hint ?? ""}`.trim(),
          camera.keyframeMeasuredAt
            ? `Measured ${formatRelative(camera.keyframeMeasuredAt, nowMs)}.`
            : "",
        ]
          .filter(Boolean)
          .join(" "),
        steps: [
          "In the camera's (or recorder's) video encoding settings for the stream Actuate pulls, set the I-frame interval to 1–2 seconds' worth of frames.",
          "Save on the camera. The new interval is measured the next time the connector reads the stream.",
        ],
        affectedSiteIds: [detail.id],
        metric: {
          value: `${lowerBound ? "> " : ""}${formatKeyframeSeconds(seconds)} s`,
          label: "between keyframes",
        },
        actions: ["copy"],
        siteId: detail.id,
        siteName: detail.name,
      });
    });
  }

  // Faults first (widest first); explanations after them.
  return findings.sort(
    (a, b) =>
      Number(a.severity === "info") - Number(b.severity === "info") ||
      b.affectedSiteIds.length - a.affectedSiteIds.length
  );
};
